import type React from 'react'
import { useCallback } from 'react'
import { Separator } from '@components/ui/separator'
import { TooltipProvider } from '@components/ui/tooltip'
import { cn } from '@/utils'
import { useFonts } from '@/providers/font'
import { AlignmentSection } from './components/alignment-section'
import { DropCapSection } from './components/drop-cap-section'
import { FontFamilyPicker } from './components/font-family-picker'
import { FontFamilySection } from './components/font-family-section'
import { FontSizeSection } from './components/font-size-section'
import { FontStyleSelect } from './components/font-style-select'
import { SpacingSection } from './components/spacing-section'
import { TextStylesSection } from './components/text-styles-section'
import { TypePanelLoadingSkeleton } from './components/type-panel-loading-skeleton'
import { useAlignmentAndDecoration } from './hooks/use-alignment-and-decoration'
import { useDefaultFontFamily } from './hooks/use-default-font-family'
import { useDropCap } from './hooks/use-drop-cap'
import { useFontAnnouncements } from './hooks/use-font-announcements'
import { useFontFamily } from './hooks/use-font-family'
import { useFontFamilyPicker } from './hooks/use-font-family-picker'
import { useFontPickerSync } from './hooks/use-font-picker-sync'
import { useFontSizeField } from './hooks/use-font-size-field'
import { useKerningField } from './hooks/use-kerning-field'
import { useLeadingField } from './hooks/use-leading-field'
import { useLeadingPresetFollow } from './hooks/use-leading-preset-follow'
import { useSelectionSync } from './hooks/use-selection-sync'
import { useStyleUpdate } from './hooks/use-style-update'
import { useTextStylesPreview } from './hooks/use-text-styles-preview'
import { useTrackingField } from './hooks/use-tracking-field'
import type { TypePanelProps } from './types/type-panel-props'

/**
 * The Type panel. Every state hook is called here, above the catalog-loading switch, so field
 * state survives the skeleton swapping in. Effects run in declaration order across hooks, so the
 * effect-bearing hooks are called last and in this order: picker (E1), announcements (E2, E3),
 * selection sync (E4, E5), picker sync (E6, E7), leading preset follow (E8).
 */
export const TypePanel: React.FC<TypePanelProps> = ({
  className,
  disabled = false,
  selectedShapeTextParagraphs,
  isSelectedShapeTextFrame = false,
  onStyleUpdate,
  spreadId,
  textPanelRevision = 0,
  shapeTextEditRevision = 0,
  defaultFontFamilyId,
  defaultFontName,
  defaultFontSize,
  restoreShapeTextFocus,
  getEditingShapeTextSelectionStyle,
  getEditingShapeTextId,
  getEditingShapeTextKerningState,
  applyKerningAtCaret,
}) => {
  const {
    isUsedCatalogLoading,
    isError: isFontCatalogError,
    failedFontFamilyNames,
    ensureFamilyFacesLoaded,
  } = useFonts()

  // True when there is a selected/editing shape with text.
  const hasTextSelectionTarget = selectedShapeTextParagraphs != null

  // Sliders / selects / popovers steal focus from the inline shape-text editor (the caret
  // vanishes). After the interaction, restore editor focus + caret/selection so editing continues
  // where it left off.
  const restoreActiveEditor = useCallback(() => {
    restoreShapeTextFocus?.()
  }, [restoreShapeTextFocus])

  const { emitStyleUpdate, recentFontFamilies } = useStyleUpdate(disabled, onStyleUpdate)
  const { resolvedDefaultFontFamilyId, defaultFontFamilyName } = useDefaultFontFamily(
    defaultFontFamilyId,
    defaultFontName
  )
  const fontFamily = useFontFamily(emitStyleUpdate)
  const fontSize = useFontSizeField({
    disabled,
    defaultFontSize,
    emitStyleUpdate,
    restoreActiveEditor,
  })
  const { fontSizeValue } = fontSize
  const leading = useLeadingField({
    disabled,
    hasTextSelectionTarget,
    defaultFontSize,
    fontSizeValue,
    emitStyleUpdate,
    restoreActiveEditor,
  })
  const kerning = useKerningField({ disabled, applyKerningAtCaret, restoreActiveEditor })
  const tracking = useTrackingField({
    disabled,
    hasTextSelectionTarget,
    emitStyleUpdate,
    restoreActiveEditor,
  })
  const alignment = useAlignmentAndDecoration({ disabled, hasTextSelectionTarget, emitStyleUpdate })
  const dropCap = useDropCap({
    disabled,
    isSelectedShapeTextFrame,
    fontSizeValue,
    emitStyleUpdate,
    restoreActiveEditor,
  })
  const textStyles = useTextStylesPreview()

  const picker = useFontFamilyPicker({
    allFamiliesSorted: fontFamily.allFamiliesSorted,
    fontFamilyIdValue: fontFamily.fontFamilyIdValue,
    resolvedDefaultFontFamilyId,
    applyFontFamilyById: fontFamily.applyFontFamilyById,
    restoreActiveEditor,
  })
  const { fontLoadAnnouncement, fontSelectionAnnouncement } = useFontAnnouncements({
    isUsedCatalogLoading,
    isFontCatalogError,
    hasTextSelectionTarget,
    fontFamilyIdMixed: fontFamily.fontFamilyIdMixed,
    currentFontDisplayName: fontFamily.currentFontDisplayName,
  })

  useSelectionSync({
    selectedShapeTextParagraphs,
    spreadId,
    defaultFontSize,
    getEditingShapeTextId,
    getEditingShapeTextSelectionStyle,
    getEditingShapeTextKerningState,
    resolvedDefaultFontFamilyId,
    textPanelRevision,
    shapeTextEditRevision,
    syncFontSizeFromLiveEdit: fontSize.syncFromLiveEdit,
    showFontSizeSelection: fontSize.showSelectionSize,
    resetFontSize: fontSize.resetToSize,
    setUnderline: alignment.setUnderline,
    setAlignment: alignment.setAlignment,
    syncKerningFromLiveEdit: kerning.syncFromLiveEdit,
    syncKerningFromParagraphs: kerning.syncFromParagraphs,
    resetKerning: kerning.resetKerning,
    setLiveEditingCollapsedCaret: tracking.setLiveEditingCollapsedCaret,
    syncTrackingFromLiveEdit: tracking.syncFromLiveEdit,
    syncTrackingFromParagraphs: tracking.syncFromParagraphs,
    resetTracking: tracking.resetTracking,
    showFamilyDisplay: fontFamily.showFamilyDisplay,
    syncLeadingFromStoredValue: leading.syncLeadingFromStoredValue,
    trackLeadingEditingSession: leading.trackEditingSession,
    resyncLeadingFromLiveEdit: leading.resyncFromLiveEdit,
    resyncLeadingFromParagraphs: leading.resyncFromParagraphs,
    clearLeading: leading.clearLeading,
    applyDropCapSummary: dropCap.applyDropCapSummary,
    resetDropCap: dropCap.resetDropCap,
  })
  useFontPickerSync(spreadId, fontFamily.fontFamilyIdValue, picker.setFontFamilySelectOpen)
  useLeadingPresetFollow({
    appliedFontSize: fontSize.appliedFontSize,
    leadingChip: leading.leadingChip,
    leadingPt: leading.leadingPt,
    setLeadingPt: leading.setLeadingPt,
    setLeadingSizePt: leading.setLeadingSizePt,
    setLeadingInputValue: leading.setLeadingInputValue,
    emitStyleUpdate,
  })

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn('space-y-4', disabled && 'pointer-events-none opacity-60', className)}
        aria-disabled={disabled}
        data-text-edit-keepalive=""
      >
        {isUsedCatalogLoading ? (
          <TypePanelLoadingSkeleton />
        ) : (
          <>
            <FontFamilySection
              isUsedCatalogLoading={isUsedCatalogLoading}
              fontLoadAnnouncement={fontLoadAnnouncement}
              fontSelectionAnnouncement={fontSelectionAnnouncement}
              recentFontFamilies={recentFontFamilies}
              failedFontFamilyNames={failedFontFamilyNames}
              disabled={disabled}
              hasTextSelectionTarget={hasTextSelectionTarget}
              fontFamilyIdValue={fontFamily.fontFamilyIdValue}
              onApplyRecent={fontFamily.applyFontFamilyById}
            >
              <FontFamilyPicker
                open={picker.fontFamilySelectOpen}
                onOpenChange={picker.handleOpenChange}
                onTriggerKeyDown={picker.handleTriggerKeyDown}
                onOpenAutoFocus={picker.handleOpenAutoFocus}
                viewportRef={picker.fontSelectViewportRef}
                onListScroll={picker.scheduleEnsureFontFamilyRowsInViewport}
                disabled={disabled}
                isUsedCatalogLoading={isUsedCatalogLoading}
                hasTextSelectionTarget={hasTextSelectionTarget}
                fontFamilyIdMixed={fontFamily.fontFamilyIdMixed}
                currentFontDisplayName={fontFamily.currentFontDisplayName}
                allFamiliesSorted={fontFamily.allFamiliesSorted}
                failedFontFamilyNames={failedFontFamilyNames}
                defaultFontFamilyName={defaultFontFamilyName}
                onSelectDefault={picker.selectDefaultFamily}
                onSelectFamily={picker.selectFamily}
                onFamilyFocus={(id) => void ensureFamilyFacesLoaded(id)}
              />
            </FontFamilySection>

            <FontStyleSelect
              value={fontFamily.variantFontIdValue}
              disabled={
                disabled ||
                !hasTextSelectionTarget ||
                (!fontFamily.sortedVariants.length && !fontFamily.mixedFamilyStyleItems.length)
              }
              fontFamilyIdMixed={fontFamily.fontFamilyIdMixed}
              variantFontIdMixed={fontFamily.variantFontIdMixed}
              variantStyleNamePlaceholder={fontFamily.variantStyleNamePlaceholder}
              sortedVariants={fontFamily.sortedVariants}
              mixedFamilyStyleItems={fontFamily.mixedFamilyStyleItems}
              onValueChange={(v) => {
                fontFamily.selectVariant(v)
                restoreActiveEditor()
              }}
              onOpenChange={(open) => {
                if (!open) restoreActiveEditor()
              }}
            />

            <Separator />

            <FontSizeSection field={fontSize} restoreActiveEditor={restoreActiveEditor} />

            <Separator />

            <SpacingSection
              leading={leading}
              kerning={kerning}
              tracking={tracking}
              hasTextSelectionTarget={hasTextSelectionTarget}
            />

            <Separator />

            <AlignmentSection field={alignment} />

            <DropCapSection controls={dropCap} />

            <Separator />

            <TextStylesSection preview={textStyles} disabled={disabled} />
          </>
        )}
      </div>
    </TooltipProvider>
  )
}
